import { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Shield, Search, Trash2, Download, AlertTriangle, Info, AlertCircle, User, Globe } from 'lucide-react'
import { API_BASE_URL } from '@/lib/apiClient'
import { useToast } from '@/contexts/ToastContext'
import { useTranslation } from 'react-i18next'

interface AuditLog {
  id: number
  user_id: string | null
  user_email: string | null
  ip_address: string | null
  user_agent: string | null
  action: string
  category: string | null
  severity: string
  description: string | null
  resource_type: string | null
  resource_id: string | null
  table_name: string | null
  record_id: string | null
  old_values: any
  new_values: any
  metadata: any
  success: boolean
  error_message: string | null
  timestamp: string
}

interface AuditResponse {
  logs: AuditLog[]
  total: number
  limit: number
  offset: number
}

const CATEGORIES = [
  { value: '', label: 'All categories' },
  { value: 'auth', label: 'Authentication' },
  { value: 'project', label: 'Projects' },
  { value: 'system', label: 'Systems' },
  { value: 'assessment', label: 'Risk Assessment' },
  { value: 'import', label: 'Import' },
  { value: 'contact', label: 'Contacts' },
  { value: 'evidence', label: 'Evidence' },
  { value: 'inventory', label: 'Inventory' },
  { value: 'admin', label: 'Admin' },
  { value: 'license', label: 'License' },
  { value: 'file', label: 'Files' },
]

const SEVERITIES = [
  { value: '', label: 'All severities' },
  { value: 'info', label: 'Info' },
  { value: 'warning', label: 'Warning' },
  { value: 'critical', label: 'Critical' },
]

const SEV = {
  info: { icon: Info, dot: 'bg-slate-400', text: 'text-slate-500' },
  warning: { icon: AlertTriangle, dot: 'bg-amber-500', text: 'text-amber-600 dark:text-amber-400' },
  critical: { icon: AlertCircle, dot: 'bg-red-500', text: 'text-red-600 dark:text-red-400' },
}

const CAT_COLORS: Record<string, string> = {
  auth: 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300',
  project: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
  system: 'bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300',
  assessment: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300',
  import: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300',
  contact: 'bg-pink-100 dark:bg-pink-900/40 text-pink-700 dark:text-pink-300',
  evidence: 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300',
  inventory: 'bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300',
  admin: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
  license: 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300',
  file: 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300',
}

function fmtTime(ts: string): string {
  const d = new Date(ts), now = new Date()
  const m = Math.floor((now.getTime() - d.getTime()) / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const dy = Math.floor(h / 24)
  if (dy < 7) return `${dy}d ago`
  return d.toLocaleDateString('en-CA') + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
}

function fmtFull(ts: string): string {
  const d = new Date(ts)
  return d.toLocaleDateString('en-CA') + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

export function AuditLogsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { success, error: showError } = useToast()
  const [logs, setLogs] = useState<AuditLog[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(0)
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const limit = 50

  const [category, setCategory] = useState('')
  const [severity, setSeverity] = useState('')
  const [searchAction, setSearchAction] = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')

  const fetchLogs = async () => {
    setLoading(true)
    try {
      const p = new URLSearchParams()
      if (category) p.set('category', category)
      if (severity) p.set('severity', severity)
      if (searchAction) p.set('action', searchAction)
      if (fromDate) p.set('from_date', fromDate)
      if (toDate) p.set('to_date', toDate)
      p.set('limit', String(limit))
      p.set('offset', String(page * limit))
      const res = await fetch(`${API_BASE_URL}/api/audit-logs?${p}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
      })
      if (!res.ok) throw new Error('Failed to fetch')
      const data: AuditResponse = await res.json()
      setLogs(data.logs)
      setTotal(data.total)
    } catch (err: any) {
      showError(err.message ?? 'Failed to load audit logs')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchLogs() }, [page, category, severity, searchAction, fromDate, toDate])

  const handleClear = async () => {
    if (!confirm('Delete ALL audit logs? This cannot be undone.')) return
    try {
      const res = await fetch(`${API_BASE_URL}/api/audit-logs/clear`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
      })
      if (!res.ok) throw new Error('Failed to clear')
      success('Audit logs cleared')
      setLogs([])
      setTotal(0)
    } catch (err: any) {
      showError(err.message ?? 'Failed to clear logs')
    }
  }

  const handleExport = () => {
    const csv = [
      ['Timestamp', 'Severity', 'Category', 'Action', 'Description', 'User', 'IP', 'Resource', 'Success'].join(','),
      ...logs.map(l => [
        fmtFull(l.timestamp), l.severity, l.category ?? '', l.action,
        `"${(l.description ?? '').replace(/"/g, '""')}"`,
        l.user_email ?? '', l.ip_address ?? '',
        l.resource_type ? `${l.resource_type}:${l.resource_id ?? ''}` : '',
        l.success ? 'yes' : 'no',
      ].join(','))
    ].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `audit-logs-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
  }

  const stats = useMemo(() => ({
    total,
    critical: logs.filter(l => l.severity === 'critical').length,
    warning: logs.filter(l => l.severity === 'warning').length,
    failed: logs.filter(l => !l.success).length,
  }), [logs, total])

  const totalPages = Math.ceil(total / limit)

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white">
      <div className="mx-auto px-4 py-4 max-w-[1400px]">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} className="p-1.5 border border-slate-200 dark:border-slate-700 rounded-md hover:bg-slate-50 dark:hover:bg-slate-800">
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div>
              <h1 className="text-lg font-semibold flex items-center gap-2"><Shield className="h-5 w-5" /> {t('dashboardPage.auditLogs')}</h1>
              <p className="text-xs text-slate-400">{t('audit.subtitle')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleExport} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-slate-200 dark:border-slate-700 rounded-md hover:bg-slate-50 dark:hover:bg-slate-800">
              <Download className="h-3.5 w-3.5" /> {t('common.export')} CSV
            </button>
            <button onClick={handleClear} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20">
              <Trash2 className="h-3.5 w-3.5" /> {t('common.clear')}
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-2 mb-3">
          {[
            { label: 'Total events', val: total.toLocaleString(), color: '' },
            { label: 'Critical', val: stats.critical, color: 'text-red-600 dark:text-red-400' },
            { label: 'Warnings', val: stats.warning, color: 'text-amber-600 dark:text-amber-400' },
            { label: 'Failed', val: stats.failed, color: 'text-red-600 dark:text-red-400' },
          ].map(s => (
            <div key={s.label} className="px-3 py-2 rounded-lg bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
              <div className="text-[10px] text-slate-400 uppercase">{s.label}</div>
              <div className={`text-lg font-semibold ${s.color}`}>{s.val}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-3">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="h-3.5 w-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input placeholder={t('adminPages.search')} value={searchAction}
              onChange={e => { setSearchAction(e.target.value); setPage(0) }}
              className="w-full h-9 pl-9 pr-3 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder:text-slate-400" />
          </div>
          <select value={category} onChange={e => { setCategory(e.target.value); setPage(0) }}
            className="h-9 px-3 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white">
            {CATEGORIES.map(c => <option key={c.value} value={c.value}>{t(`adminPages.category.${c.value || 'all'}`, { defaultValue: c.label })}</option>)}
          </select>
          <select value={severity} onChange={e => { setSeverity(e.target.value); setPage(0) }}
            className="h-9 px-3 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white">
            {SEVERITIES.map(s => <option key={s.value} value={s.value}>{t(`adminPages.severity.${s.value || 'all'}`, { defaultValue: s.label })}</option>)}
          </select>
          <input type="date" value={fromDate} onChange={e => { setFromDate(e.target.value); setPage(0) }}
            className="h-9 px-3 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white" />
          <input type="date" value={toDate} onChange={e => { setToDate(e.target.value); setPage(0) }}
            className="h-9 px-3 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white" />
          {(category || severity || searchAction || fromDate || toDate) && (
            <button onClick={() => { setCategory(''); setSeverity(''); setSearchAction(''); setFromDate(''); setToDate(''); setPage(0) }}
              className="h-9 px-3 text-xs text-slate-400 hover:text-slate-600">{t('common.clear')}</button>
          )}
        </div>

        {/* Log list */}
        {loading ? (
          <div className="py-16 text-center text-slate-400">{t('adminPages.loading')}</div>
        ) : logs.length === 0 ? (
          <div className="py-16 text-center">
            <Shield className="h-10 w-10 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
            <p className="text-sm text-slate-400">{t('adminPages.noResults')}</p>
          </div>
        ) : (
          <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden bg-white dark:bg-slate-800/30">
            {logs.map((log, i) => {
              const sev = SEV[log.severity as keyof typeof SEV] ?? SEV.info
              const catColor = CAT_COLORS[log.category ?? ''] ?? CAT_COLORS.file
              const expanded = expandedId === log.id
              return (
                <div key={log.id} className={`${i > 0 ? 'border-t border-slate-100 dark:border-slate-700/50' : ''} ${!log.success ? 'bg-red-50/30 dark:bg-red-900/10' : ''}`}>
                  <button onClick={() => setExpandedId(expanded ? null : log.id)}
                    className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-800/60">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${sev.dot}`} />
                    <div className="w-16 flex-shrink-0 text-[10px] text-slate-400" title={fmtFull(log.timestamp)}>{fmtTime(log.timestamp)}</div>
                    <div className="w-20 flex-shrink-0">
                      {log.category && <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${catColor}`}>{log.category}</span>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-slate-700 dark:text-slate-200 truncate">{log.action.replace(/_/g, ' ')}</div>
                      {log.description && <div className="text-[10px] text-slate-400 truncate">{log.description}</div>}
                    </div>
                    <div className="hidden sm:block w-40 flex-shrink-0">
                      <div className="text-[10px] text-slate-500 truncate flex items-center gap-1"><User className="h-2.5 w-2.5" />{log.user_email ?? 'anonymous'}</div>
                    </div>
                    <div className="hidden md:block w-28 flex-shrink-0">
                      <div className="text-[10px] text-slate-400 truncate flex items-center gap-1"><Globe className="h-2.5 w-2.5" />{log.ip_address ?? '—'}</div>
                    </div>
                    <div className="w-6 flex-shrink-0 text-center">
                      {!log.success && <span className="text-[9px] font-semibold text-red-500">{t('audit.fail')}</span>}
                    </div>
                  </button>
                  {expanded && (
                    <div className="px-3 pb-3 pt-0 ml-5 border-l-2 border-slate-200 dark:border-slate-700">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 text-[11px] mb-2">
                        <div><span className="text-slate-400 block">{t('audit.timestamp')}</span><span>{fmtFull(log.timestamp)}</span></div>
                        <div><span className="text-slate-400 block">{t('licenseAdmin.user')}</span><span>{log.user_email ?? '—'}</span></div>
                        <div><span className="text-slate-400 block">IP address</span><span>{log.ip_address ?? '—'}</span></div>
                        <div><span className="text-slate-400 block">{t('audit.severity')}</span><span className={`inline-flex items-center gap-1 ${sev.text}`}><sev.icon className="h-3 w-3" />{log.severity}</span></div>
                        {log.resource_type && <div><span className="text-slate-400 block">{t('audit.resource')}</span><span>{log.resource_type}{log.resource_id ? `: ${log.resource_id.slice(0, 8)}...` : ''}</span></div>}
                        {log.error_message && <div className="col-span-2"><span className="text-slate-400 block">{t('moc.error')}</span><span className="text-red-600 dark:text-red-400">{log.error_message}</span></div>}
                      </div>
                      {log.user_agent && <div className="text-[10px] text-slate-400 mb-2 truncate">UA: {log.user_agent}</div>}
                      {(log.old_values || log.new_values || log.metadata) && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                          {log.old_values && <div><span className="text-[10px] text-slate-400 block mb-1">{t('audit.previousValues')}</span><pre className="text-[10px] bg-slate-50 dark:bg-slate-900/40 p-2 rounded overflow-x-auto max-h-32">{JSON.stringify(log.old_values, null, 2)}</pre></div>}
                          {log.new_values && <div><span className="text-[10px] text-slate-400 block mb-1">{t('audit.newValues')}</span><pre className="text-[10px] bg-slate-50 dark:bg-slate-900/40 p-2 rounded overflow-x-auto max-h-32">{JSON.stringify(log.new_values, null, 2)}</pre></div>}
                          {log.metadata && <div><span className="text-[10px] text-slate-400 block mb-1">{t('audit.metadata')}</span><pre className="text-[10px] bg-slate-50 dark:bg-slate-900/40 p-2 rounded overflow-x-auto max-h-32">{JSON.stringify(log.metadata, null, 2)}</pre></div>}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-3 text-xs text-slate-400">
            <span>Showing {page * limit + 1}–{Math.min((page + 1) * limit, total)} of {total.toLocaleString()}</span>
            <div className="flex gap-1">
              <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                className="px-2 py-1 border border-slate-200 dark:border-slate-700 rounded hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-30">{t('common.previous')}</button>
              <button onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}
                className="px-2 py-1 border border-slate-200 dark:border-slate-700 rounded hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-30">{t('common.next')}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
